import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, CheckCircle, Award, Clock } from "lucide-react";
import heroWindshield from "@/assets/hero-windshield.webp";
import SEO from "@/components/SEO";

const WindshieldProtection = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Windshield Protection Film Las Vegas | Battle Born Protection Films"
        description="Windshield PPF in Las Vegas prevents rock chip cracks. Avoid costly windshield replacement. XPEL certified. Call (702) 559-9365."
        canonical="https://battlebornppf.com/services/windshield-protection"
      />
      <link rel="preload" as="image" href={heroWindshield} type="image/webp" />
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroWindshield} 
            alt="XPEL windshield protection film installation preventing rock chips in Las Vegas" 
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-darker-bg/90 to-background/95" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 text-foreground">
              Windshield Protection Film Las Vegas
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Prevent Rock Chip Cracks Before They Start
            </p>
            <Button asChild size="lg" variant="premium">
              <Link to="/contact">Request Free Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Windshield PPF Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 uppercase">
              Why Windshield PPF in Las Vegas?
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-12">
              Las Vegas drivers face a constant barrage of road debris on I-15, US-95, and the 215 Beltway. A single rock chip in the wrong spot can spider-crack across your windshield — and with ADAS-equipped vehicles, windshield replacement now costs $800–$2,500 including recalibration. Windshield protection film absorbs the impact of small rocks and debris, preventing chips from forming or spreading. Battle Born Protection Films installs optically clear windshield PPF that does not distort visibility, interfere with sensors, or affect wiper performance.
            </p>

            <h2 className="text-4xl font-heading font-bold mb-8 uppercase">
              Benefits of Windshield Film
            </h2>
            <ul className="space-y-4 mb-16">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground text-lg">Prevents chips from road debris on Las Vegas highways</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground text-lg">Saves $800–$2,500 in windshield replacement and ADAS recalibration</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground text-lg">Crystal clear — no distortion, no interference with sensors or cameras</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground text-lg">Reduces glare and blocks UV for a more comfortable drive</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <Shield className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Ultimate Windshield Defense
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed">
                A rock chip can lead to a costly windshield replacement. Our durable, optically clear windshield protection film, made from XPEL technology, is engineered to absorb impacts and stop damage before it starts. This film provides an invisible layer of defense against rock chips, pitting, and scratches, all without distorting your view of the road.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="bg-card border-border">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold mb-4">The Problem</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <span className="text-accent mr-2">•</span>
                      <span>Rock chips from highway debris</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-accent mr-2">•</span>
                      <span>Pitting from gravel and sand</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-accent mr-2">•</span>
                      <span>Small chips quickly spread into cracks</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-accent mr-2">•</span>
                      <span>Windshield replacement costs $300-$1,000+</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-accent mr-2">•</span>
                      <span>Reduced visibility and safety concerns</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-card border-accent/50">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold mb-4">The Solution</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>8-mil thick protective barrier</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Absorbs impact from road debris</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Prevents chips from spreading</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>One-time investment protects for years</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span>Crystal clear - no visibility issues</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-background to-darker-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Protection Benefits
              </h2>
              <p className="text-xl text-muted-foreground">
                Comprehensive defense for your windshield investment
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <Shield className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Prevents Rock Chip Damage</h3>
                  <p className="text-muted-foreground">
                    8-mil thick film absorbs the impact of flying debris, preventing chips and cracks before they start
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <CheckCircle className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Stops Chips From Spreading</h3>
                  <p className="text-muted-foreground">
                    If a chip does occur, the film helps contain it and prevents it from spreading into costly cracks
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <Award className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Optically Clear</h3>
                  <p className="text-muted-foreground">
                    Crystal-clear film with no distortion - maintains perfect visibility in all driving conditions
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <CheckCircle className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">UV Protection</h3>
                  <p className="text-muted-foreground">
                    Blocks harmful UV rays that can damage your windshield and interior over time
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <CheckCircle className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Self-Healing Properties</h3>
                  <p className="text-muted-foreground">
                    Minor scratches and swirl marks disappear with heat, keeping your windshield looking pristine
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <CheckCircle className="w-12 h-12 text-accent mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-3">Cost-Effective</h3>
                  <p className="text-muted-foreground">
                    Much more affordable than windshield replacement - protect your investment for years to come
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section with Video */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                XPEL Film Technology
              </h2>
              <p className="text-xl text-muted-foreground">
                Advanced protection engineered for your windshield
              </p>
            </div>

            {/* Video Section */}
            <div className="mb-16">
              <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-silver bg-darker-bg">
              <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/JcDNc5flJ70"
                  title="XPEL Windshield Protection Film"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                Watch how XPEL Windshield Protection Film defends against road hazards
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-heading font-bold mb-6">Technical Specifications</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">8-Mil Thickness</p>
                      <p className="text-muted-foreground">Engineered to absorb high-impact debris</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Self-Healing Top Coat</p>
                      <p className="text-muted-foreground">Minor scratches disappear with heat exposure</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Optical Clarity</p>
                      <p className="text-muted-foreground">99.9% light transmission with zero distortion</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">UV Resistant</p>
                      <p className="text-muted-foreground">Won't yellow or haze over time</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-heading font-bold mb-6">Why XPEL?</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Industry Leader</p>
                      <p className="text-muted-foreground">Trusted by luxury and exotic car owners worldwide</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Proven Track Record</p>
                      <p className="text-muted-foreground">Decades of protection film innovation and testing</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Manufacturer Warranty</p>
                      <p className="text-muted-foreground">Backed by comprehensive warranty coverage</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Durability Tested</p>
                      <p className="text-muted-foreground">Rigorously tested in extreme conditions</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Windshield Protection Section */}
      <section className="py-20 bg-gradient-to-b from-darker-bg to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Why Windshield Protection Matters
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-accent mb-2">70%</div>
                  <p className="text-muted-foreground">
                    of windshield damage starts with a small chip that could have been prevented
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-accent mb-2">$500+</div>
                  <p className="text-muted-foreground">
                    average cost of windshield replacement vs. one-time film investment
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-bold text-accent mb-2">5+ Years</div>
                  <p className="text-muted-foreground">
                    of continuous protection from a single windshield film installation
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-card border-accent/50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold mb-4">Perfect For</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">Frequent highway drivers</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">Luxury and exotic vehicles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">Leased vehicles (protects resale value)</span>
                    </li>
                  </ul>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">Commercial fleet vehicles</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">New vehicle owners</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-accent mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">Anyone wanting to avoid costly repairs</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Process Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Professional Installation Process
              </h2>
              <p className="text-xl text-muted-foreground">
                Expert application ensures optimal protection and clarity
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent">1</span>
                </div>
                <h3 className="text-lg font-heading font-bold mb-2">Inspection</h3>
                <p className="text-muted-foreground text-sm">
                  Thorough windshield inspection and cleaning
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent">2</span>
                </div>
                <h3 className="text-lg font-heading font-bold mb-2">Preparation</h3>
                <p className="text-muted-foreground text-sm">
                  Surface prep and computer-cut pattern fitting
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent">3</span>
                </div>
                <h3 className="text-lg font-heading font-bold mb-2">Application</h3>
                <p className="text-muted-foreground text-sm">
                  Precision installation for bubble-free finish
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-accent">4</span>
                </div>
                <h3 className="text-lg font-heading font-bold mb-2">Quality Check</h3>
                <p className="text-muted-foreground text-sm">
                  Final inspection and care instructions
                </p>
              </div>
            </div>

            <Card className="bg-card border-border mt-12">
              <CardContent className="p-8">
                <div className="flex items-start">
                  <Clock className="w-8 h-8 text-accent mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-bold mb-2">Installation Time</h3>
                    <p className="text-muted-foreground">
                      Typical windshield protection film installation takes 1-2 hours. We use computer-cut patterns designed specifically for your vehicle's make and model, ensuring perfect edge-to-edge coverage without any cutting on the vehicle.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Protect Your Windshield Today
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Prevent costly windshield damage with professional protection film installation
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Windshield Protection Film Installation",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6960 W Russell Rd B",
              "addressLocality": "Las Vegas",
              "addressRegion": "NV",
              "postalCode": "89113",
              "addressCountry": "US"
            }
          },
          "areaServed": {
            "@type": "City",
            "name": "Las Vegas"
          },
          "description": "Professional windshield protection film installation using XPEL technology. Prevent rock chips and cracks with optically clear, durable protection film."
        })}
      </script>
    </div>
  );
};

export default WindshieldProtection;
